import React, { useState, useEffect, useCallback } from 'react';
import { eventsAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { formatDateShortEST } from '../utils/dateUtils';

const Home: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState<any[]>([]);

  const resolveEventImageUrl = (imageFiles?: string) => {
    if (!imageFiles) return '/img/bg-img/bg-0.png';
    if (/^https?:\/\//i.test(imageFiles)) return imageFiles;
    // Paths stored as /img/events-img/... are served directly by the frontend
    if (imageFiles.startsWith('/')) return imageFiles;
    return `/img/events-img/${imageFiles}`;
  };

  const fetchCurrentEvents = useCallback(async () => {
      try {
        setLoading(true);
        const response = await eventsAPI.getCurrentEvents({
          page: 1,
          limit: 6,
          search: '',
        });
        setEvents(response.data.events);
      } catch (err: any) {
        // Silently handle error
      } finally {
        setLoading(false);
      }
    }, []);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCurrentEvents();
  }, [fetchCurrentEvents]);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>SoundX724 — Where Music Meets Soul</title>
        <meta name="description" content="Discover Indian classical music concerts and events in your area. Book tickets for upcoming performances at SoundX724." />
        <link rel="canonical" href="https://www.soundx724.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="SoundX724 — Where Music Meets Soul" />
        <meta property="og:description" content="Discover Indian classical music concerts and events in your area. Book tickets for upcoming performances at SoundX724." />
        <meta property="og:url" content="https://www.soundx724.com/" />
        <meta property="og:image" content="https://www.soundx724.com/img/bg-img/bg-0.png" />
        <meta property="og:site_name" content="SoundX724" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-secondary-800 via-primary-700 to-warm-800 text-white overflow-hidden">
        {/* Background image overlay */}
        <img
          src="/img/bg-img/bg-0.png"
          alt="Indian Classical Music Background"
          className="absolute inset-0 w-full h-full object-cover opacity-40 pointer-events-none z-0"
        />
        {/* Decorative pattern overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-transparent to-warm-600/20 z-10"></div>
        <div className="relative container mx-auto px-6 py-32 z-20">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-6xl md:text-8xl font-elegant font-bold mb-8 leading-tight">
              Where music meets soul             
            </h1>
            <p className="text-xl md:text-2xl text-warm-100 mb-12 leading-relaxed max-w-3xl mx-auto">
              Immerse yourself in the timeless melodies and rhythms that have captivated hearts for centuries. 
              Discover the profound beauty of India's musical heritage.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link
                to="/events"
                className="bg-gradient-to-r from-warm-500 via-warm-600 to-accent-600 hover:from-warm-600 hover:via-warm-700 hover:to-accent-700 text-white px-10 py-5 rounded-2xl text-xl font-bold transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-warm-500/25"
              >
                Explore Events
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative wave at bottom */}
        <div className="absolute bottom-0 w-full">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-16">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-white"></path>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-br from-white via-warm-50 to-primary-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-elegant font-bold text-secondary-800 mb-6">
              Discover Musical Excellence
            </h2>
            <p className="text-xl text-secondary-600 max-w-4xl mx-auto leading-relaxed">
              From intimate recitals to grand concerts, explore a world of classical Indian music 
              where tradition meets contemporary artistry
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <div className="group text-center p-10 rounded-3xl bg-gradient-to-br from-white via-warm-50 to-primary-100 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-warm-200/50">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-600 to-warm-700 rounded-2xl flex items-center justify-center mx-auto mb-8 group-hover:rotate-6 transition-transform duration-300">
                <span className="text-3xl text-white">🎼</span>
              </div>
              <h3 className="text-2xl font-bold text-secondary-800 mb-6">Live Concerts</h3>
              <p className="text-secondary-600 leading-relaxed">
                Experience world-class performances by renowned classical musicians in carefully curated intimate venues
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group text-center p-10 rounded-3xl bg-gradient-to-br from-white via-accent-50 to-warm-100 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-accent-200/50">
              <div className="w-20 h-20 bg-gradient-to-br from-accent-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-8 group-hover:rotate-6 transition-transform duration-300">
                <span className="text-3xl text-white">📚</span>
              </div>
              <h3 className="text-2xl font-bold text-secondary-800 mb-6">Music Education</h3>
              <p className="text-secondary-600 leading-relaxed">
                Learn about ragas, talas, and the rich traditions through immersive workshops and masterclasses
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group text-center p-10 rounded-3xl bg-gradient-to-br from-white via-secondary-100 to-primary-100 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-secondary-200/50">
              <div className="w-20 h-20 bg-gradient-to-br from-secondary-600 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-8 group-hover:rotate-6 transition-transform duration-300">
                <span className="text-3xl text-white">🌟</span>
              </div>
              <h3 className="text-2xl font-bold text-secondary-800 mb-6">Cultural Heritage</h3>
              <p className="text-secondary-600 leading-relaxed">
                Preserve and celebrate the ancient traditions of Indian classical music for future generations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Preview */}
      <section className="py-20 bg-gradient-to-r from-primary-50 to-yellow-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-elegant font-bold text-secondary-900 mb-4">
              Upcoming Events
            </h2>
            <p className="text-xl text-secondary-600">
              Don't miss these enchanting musical experiences
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {loading ? (
              <div className="col-span-3 text-center py-12 text-secondary-400 text-lg">Loading events...</div>
            ) : events.length === 0 ? (
              <div className="col-span-3 text-center py-12 text-secondary-400 text-lg">No upcoming events scheduled</div>
            ) : (
              events.map((event: any) => (
                <div key={event.uuid} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
                  <div className="h-48 bg-gradient-to-br from-primary-400 to-primary-600 overflow-hidden">
                    <img
                      src={resolveEventImageUrl(event.image_files)}
                      alt={event.title || 'Event image'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '/img/bg-img/bg-0.png';
                      }}
                    />
                  </div>
                  <div className="p-6 flex gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                        {event.title || 'Untitled Event'}
                      </h3>
                      <p className="text-secondary-600 text-sm mb-4">
                        {event.description || 'No description available.'}
                      </p>
                      <div className="flex justify-between items-center text-sm text-secondary-500">
                        <span>📅 {event.event_start_date ? formatDateShortEST(event.event_start_date) : 'TBA'}</span>
                        <span>{event.price ? `CAD $${event.price}` : ''}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 items-end justify-center">
                      {new Date(event.event_start_date) > new Date() && event.is_disabled === false && (
                        <Link
                          to={`/book-tickets/${event.uuid}`}
                          onClick={() => window.scrollTo(0, 0)}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm font-semibold whitespace-nowrap"
                          title="Book Tickets"
                        >
                          Book
                        </Link>
                      )}
                      <Link
                        to={`/events/${event.uuid}`}
                        className="w-10 h-10 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                        title="View Details"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="text-center">
            <Link
              to="/events"
              className="bg-primary-600 hover:bg-primary-500 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              View All Events
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-secondary-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-elegant font-bold mb-4">
            Stay Connected with Classical Music
          </h2>
          <p className="text-xl text-secondary-300 mb-8 max-w-2xl mx-auto">
            Get updates about upcoming events, artist interviews, and musical insights delivered to your inbox
          </p>
          <div className="max-w-md mx-auto flex gap-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <button className="bg-primary-600 hover:bg-primary-500 px-6 py-3 rounded-lg font-semibold transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;